import React, { useEffect, useId, useLayoutEffect, useRef, useState } from "react";

function BrainChipIcon() {
  const maskId = useId();
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" aria-hidden="true">
      <path
        d="M8 1.5V4M12 1.5V4M16 1.5V4M8 20v2.5M12 20v2.5M16 20v2.5M1.5 8H4M1.5 12H4M1.5 16H4M20 8h2.5M20 12h2.5M20 16h2.5"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
      />
      <mask id={maskId}>
        <rect x="3.5" y="3.5" width="17" height="17" rx="3" fill="#fff" />
        <path
          d="M11.3 6.8c-1.5-.6-3 .3-3.2 1.7-1.2.4-1.7 1.8-1.1 2.9-.7 1-.3 2.5.9 3 .2 1.6 1.9 2.6 3.4 1.8z"
          fill="#000"
        />
        <path
          d="M12.7 6.8c1.5-.6 3 .3 3.2 1.7 1.2.4 1.7 1.8 1.1 2.9.7 1 .3 2.5-.9 3-.2 1.6-1.9 2.6-3.4 1.8z"
          fill="#000"
        />
      </mask>
      <rect x="3.5" y="3.5" width="17" height="17" rx="3" fill="currentColor" mask={`url(#${maskId})`} />
    </svg>
  );
}

const POPOVER_EDGE_GAP = 12;

interface TierScoreProps {
  tierLabel: string;
  score: number;
  modelCount: number;
  updatedAt: string;
}

export default function TierScore({ tierLabel, score, modelCount, updatedAt }: TierScoreProps) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLSpanElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const popoverId = useId();

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: PointerEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      setOpen(false);
      buttonRef.current?.focus();
    };
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  // The wizard's scroll area clips sideways, so pull the popover back inside it.
  useLayoutEffect(() => {
    const popover = popoverRef.current;
    if (!open || !popover) return;
    popover.style.left = "0px";
    const bounds = popover.closest(".wizard-scroll")?.getBoundingClientRect();
    const rect = popover.getBoundingClientRect();
    const right = bounds ? bounds.right : window.innerWidth;
    const overflow = rect.right + POPOVER_EDGE_GAP - right;
    if (overflow > 0) popover.style.left = `${-overflow}px`;
    popover.scrollIntoView?.({ block: "nearest" });
  }, [open]);

  const updated = new Date(updatedAt).toLocaleDateString(undefined, { month: "short", day: "numeric" });

  return (
    <span
      ref={rootRef}
      className={`tier-score ${open ? "is-open" : ""}`}
      onBlur={(e) => {
        if (e.relatedTarget && !e.currentTarget.contains(e.relatedTarget as Node)) setOpen(false);
      }}
    >
      <button
        ref={buttonRef}
        type="button"
        className="tier-score-button"
        aria-label={`${tierLabel} intelligence score: ${score} out of 100. About this score`}
        aria-expanded={open}
        aria-controls={popoverId}
        onClick={() => setOpen((v) => !v)}
      >
        <BrainChipIcon />
        <span>{score}</span>
      </button>
      <div ref={popoverRef} id={popoverId} className="tier-score-popover" hidden={!open}>
        <p className="tier-score-title">Intelligence score: {score} of 100</p>
        <p>
          The average Artificial Analysis Intelligence Index of the {modelCount} models {tierLabel} debates can
          use. Higher scores mean stronger results on reasoning and knowledge benchmarks.
        </p>
        <p>Use it to compare tiers. It isn't a percentage grade.</p>
        <p className="tier-score-source">
          Based on data from{" "}
          <a href="https://artificialanalysis.ai/" target="_blank" rel="noopener noreferrer">
            Artificial Analysis
          </a>
          . We calculate this average ourselves; it doesn't represent the views or endorsement of Artificial
          Analysis. Updated {updated}.
        </p>
      </div>
    </span>
  );
}
